import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { generateBrokerAgreementWordDocument } from "@/utils/brokerAgreementWordGenerator";

export interface BreakdownItem {
  label: string;
  amount: string;
}

export interface BrokerAgreementData {
  brokerName: string;
  investorName: string;
  sellerName: string;
  buyerName: string;
  dateOfSale: string;
  salePrice: string;
  downPayment: string;
  interestRate: string;
  piPayment: string;
  firstPaymentDate: string;
  firstPaymentToInvestor: string;
  netOffer: string;
  breakdownItems: BreakdownItem[];
  agreementDate: string;
  signedByName: string;
  signedByCompany: string;
}

export function BrokerAgreementGenerator() {
  const [deals, setDeals] = useState<any[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  
  const [formData, setFormData] = useState<BrokerAgreementData>({
    brokerName: "Contract Funding Group",
    investorName: "First National Bank of America",
    sellerName: "",
    buyerName: "",
    dateOfSale: "",
    salePrice: "",
    downPayment: "",
    interestRate: "",
    piPayment: "",
    firstPaymentDate: "",
    firstPaymentToInvestor: "",
    netOffer: "",
    breakdownItems: [
      { label: "Seller's agreed upon amount", amount: "" },
      { label: "Broker's proceeds", amount: "" },
    ],
    agreementDate: "",
    signedByName: "Mike Watters",
    signedByCompany: "Contract Funding Group",
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDeals(data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error('Failed to load deals');
    }
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const selectedDeal = deals.find(d => d.id === dealId);
    
    if (selectedDeal) {
      setFormData(prev => ({
        ...prev,
        sellerName: selectedDeal.seller_name || "",
        buyerName: selectedDeal.buyer_name || "",
        dateOfSale: selectedDeal.date_of_sale || "",
        salePrice: selectedDeal.sales_price?.toString() || "",
        downPayment: selectedDeal.down_payment_amount?.toString() || "",
        interestRate: selectedDeal.interest_rate?.toString() || "",
        piPayment: selectedDeal.payment_amount?.toString() || "",
        firstPaymentDate: selectedDeal.first_payment_date || "",
        firstPaymentToInvestor: selectedDeal.first_payment_to_me || "",
        netOffer: selectedDeal.offer_price?.toString() || "",
      }));
      
      toast.success("Deal data loaded successfully");
    }
  };

  const handleInputChange = (field: keyof BrokerAgreementData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBreakdownItemChange = (index: number, field: 'label' | 'amount', value: string) => {
    setFormData(prev => ({
      ...prev,
      breakdownItems: prev.breakdownItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addBreakdownItem = () => {
    setFormData(prev => ({
      ...prev,
      breakdownItems: [...prev.breakdownItems, { label: "", amount: "" }]
    }));
  };

  const removeBreakdownItem = (index: number) => {
    if (formData.breakdownItems.length <= 1) {
      toast.error("Must have at least one breakdown item");
      return;
    }
    setFormData(prev => ({
      ...prev,
      breakdownItems: prev.breakdownItems.filter((_, i) => i !== index)
    }));
  };

  const handleGenerateDocument = async () => {
    try {
      // Validate required fields
      if (!formData.sellerName || !formData.buyerName || !formData.dateOfSale) {
        toast.error("Please fill in all required fields");
        return;
      }

      const blob = await generateBrokerAgreementWordDocument(formData);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Broker_Agreement_${formData.sellerName.replace(/\s+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Broker agreement generated successfully!");
    } catch (error) {
      console.error('Error generating broker agreement:', error);
      toast.error("Failed to generate broker agreement");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Broker Agreement Generator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Deal Selector */}
          <div className="space-y-2">
            <Label htmlFor="deal-selector">Load from Deal (Optional)</Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger id="deal-selector">
                <SelectValue placeholder="Select a deal to auto-fill..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Select a deal to automatically populate the form fields
            </p>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="brokerName">Broker Name</Label>
              <Input
                id="brokerName"
                value={formData.brokerName}
                onChange={(e) => handleInputChange("brokerName", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="investorName">Investor Name</Label>
              <Input
                id="investorName"
                value={formData.investorName}
                onChange={(e) => handleInputChange("investorName", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sellerName">Seller Name(s) *</Label>
              <Input
                id="sellerName"
                placeholder="e.g., John Smith and Jane Smith"
                value={formData.sellerName}
                onChange={(e) => handleInputChange("sellerName", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="buyerName">Buyer Name(s) *</Label>
              <Input
                id="buyerName"
                placeholder="e.g., Bob Johnson"
                value={formData.buyerName}
                onChange={(e) => handleInputChange("buyerName", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfSale">Date of Sale *</Label>
              <Input
                id="dateOfSale"
                type="date"
                value={formData.dateOfSale}
                onChange={(e) => handleInputChange("dateOfSale", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="salePrice">Sale Price</Label>
              <Input
                id="salePrice"
                type="number"
                placeholder="155000"
                value={formData.salePrice}
                onChange={(e) => handleInputChange("salePrice", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="downPayment">Down Payment</Label>
              <Input
                id="downPayment"
                type="number"
                placeholder="30000"
                value={formData.downPayment}
                onChange={(e) => handleInputChange("downPayment", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="interestRate">Interest Rate (%)</Label>
              <Input
                id="interestRate"
                type="number"
                step="0.01"
                placeholder="8.00"
                value={formData.interestRate}
                onChange={(e) => handleInputChange("interestRate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="piPayment">P&I Payment</Label>
              <Input
                id="piPayment"
                type="number"
                placeholder="2000"
                value={formData.piPayment}
                onChange={(e) => handleInputChange("piPayment", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstPaymentDate">Date of First Payment</Label>
              <Input
                id="firstPaymentDate"
                type="date"
                value={formData.firstPaymentDate}
                onChange={(e) => handleInputChange("firstPaymentDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstPaymentToInvestor">First Payment to Investor</Label>
              <Input
                id="firstPaymentToInvestor"
                type="date"
                value={formData.firstPaymentToInvestor}
                onChange={(e) => handleInputChange("firstPaymentToInvestor", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="netOffer">Net Offer Amount</Label>
              <Input
                id="netOffer"
                type="number"
                placeholder="102500"
                value={formData.netOffer}
                onChange={(e) => handleInputChange("netOffer", e.target.value)}
              />
            </div>

            <div className="col-span-full">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-semibold">Payment Breakdown Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBreakdownItem}
                >
                  Add Item
                </Button>
              </div>
              
              <div className="space-y-4">
                {formData.breakdownItems.map((item, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`breakdown-label-${index}`}>
                        {String.fromCharCode(97 + index)}. Description
                      </Label>
                      <Input
                        id={`breakdown-label-${index}`}
                        placeholder={index === 0 ? "Seller's agreed upon amount" : index === 1 ? "Broker's proceeds" : "Item description"}
                        value={item.label}
                        onChange={(e) => handleBreakdownItemChange(index, 'label', e.target.value)}
                      />
                    </div>
                    <div className="w-48 space-y-2">
                      <Label htmlFor={`breakdown-amount-${index}`}>Amount</Label>
                      <Input
                        id={`breakdown-amount-${index}`}
                        type="number"
                        placeholder="0"
                        value={item.amount}
                        onChange={(e) => handleBreakdownItemChange(index, 'amount', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBreakdownItem(index)}
                      disabled={formData.breakdownItems.length <= 1}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="agreementDate">Agreement Date</Label>
              <Input
                id="agreementDate"
                type="date"
                value={formData.agreementDate}
                onChange={(e) => handleInputChange("agreementDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signedByName">Signed By Name</Label>
              <Input
                id="signedByName"
                value={formData.signedByName}
                onChange={(e) => handleInputChange("signedByName", e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleGenerateDocument} className="w-full">
            <FileText className="mr-2 h-4 w-4" />
            Generate Broker Agreement
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
